import { DateType } from 'api/models';
import {
  ResourceIdentifier,
  ResourceType,
} from 'api/resource-management/types';
import { aggregationIdForUser } from 'queries/resource-management';
import { SchedulerResourceType } from '../resource-types';
import { ViewPresetId } from '../scheduler-config/view-presets/constants';
import { UserProjectResourceIdentifier } from './events-conversion/resource-summary/project-summary';

export interface UserAsLeafResourceProps {
  projectId: number;
  userId: number;
  roleId?: number;
  referenceTeamId?: number;
  budgetId?: number;
}

export interface ProjectAsLeafResourceProps {
  projectId: number;
  userId: number;
  roleId?: number;
  referenceTeamId?: number;
  budgetId?: number;
}

export const projectIdFormat = (id: number): string => `project-${id}`;
export const userIdFormat = (id: number): string => `user-${id}`;
export const teamIdFormat = (id: number): string => `team-${id}`;
export const roleIdFormat = (id: number): string => `role-${id}`;
export const budgetIdFormat = (id: number): string => `budget-${id}`;
export const companyIdFormat = (id: number): string => `company-${id}`;
export const referenceTeamIdFormat = (id: number): string =>
  `referenceTeam-${id}`;
export const aggregationId = (
  type: ResourceType,
  projectId: number,
  roleId?: number,
  referenceTeamId?: number,
): string =>
  `[project-${projectId}${
    type === ResourceType.User
      ? `${roleId ? `_role-${roleId}` : ''}${
          referenceTeamId ? `_referenceTeam-${referenceTeamId}` : ''
        }`
      : ''
  }`;
export const resourceIdForRole = (projectId: number, roleId: number): string =>
  `${projectIdFormat(projectId)}_${roleIdFormat(roleId)}`;

export const resourceIdForFinancialBudget = (
  projectId: number,
  budgetId: number,
): string => `${projectIdFormat(projectId)}_${budgetIdFormat(budgetId)}`;

export const resourceIdForProject = (projectId: number): string =>
  projectIdFormat(projectId);
export const resourceIdForUser = (userId: number): string =>
  userIdFormat(userId);
export const resourceIdForTeam = (teamId: number): string =>
  teamIdFormat(teamId);
export const resourceIdForCompany = (companyId: number): string =>
  companyIdFormat(companyId);
export const resourceIdForProjectAsLeaf = ({
  projectId,
  userId,
  roleId,
  referenceTeamId,
  budgetId,
}: ProjectAsLeafResourceProps): string =>
  `${projectIdFormat(projectId)}${
    budgetId ? `_${budgetIdFormat(budgetId)}` : ''
  }_${userIdFormat(userId)}${roleId ? `_${roleIdFormat(roleId)}` : ''}${
    referenceTeamId ? `_${referenceTeamIdFormat(referenceTeamId)}` : ''
  }`;

export const resourceIdForBudgetAsLeaf = resourceIdForProjectAsLeaf;
export const resourceIdForProjectOnUsers = ({
  projectId,
  userId,
  roleId,
  referenceTeamId,
}: Partial<ProjectAsLeafResourceProps> & { projectId: number }): string =>
  `${projectIdFormat(projectId)}${userId ? `_${userIdFormat(userId)}` : ''}${
    roleId ? `_${roleIdFormat(roleId)}` : ''
  }${referenceTeamId ? `_${referenceTeamIdFormat(referenceTeamId)}` : ''}`;
export const resourceIdForUserAsLeaf = ({
  projectId,
  userId,
  roleId,
  referenceTeamId,
  budgetId,
}: UserAsLeafResourceProps): string =>
  `${projectIdFormat(projectId)}${
    budgetId ? `_${budgetIdFormat(budgetId)}` : ''
  }_${userIdFormat(userId)}${roleId ? `_${roleIdFormat(roleId)}` : ''}${
    referenceTeamId ? `_${referenceTeamIdFormat(referenceTeamId)}` : ''
  }`;
export const resourceIdForTeamAsLeaf = (
  projectId: number,
  teamId: number,
  budgetId?: number,
): string =>
  `${projectIdFormat(projectId)}${
    budgetId ? `_${budgetIdFormat(budgetId)}` : ''
  }_${teamIdFormat(teamId)}`;
export const resourceIdForAssignUserOrTeam = (
  projectId: number,
  budgetId?: number,
): string =>
  `${projectIdFormat(projectId)}${
    budgetId ? `_${budgetIdFormat(budgetId)}` : ''
  }_assign_user_or_team`;
export const resourceIdForShowAllProjects = (roleId: number): string =>
  `${roleIdFormat(roleId)}_show_all_projects`;

export const resourceIdForProjectsUnderPeopleLoader = ({
  userId,
  page = 1,
}: {
  userId: number;
  page?: number;
}): string =>
  `${userIdFormat(userId)}_projects_under_people_loader_page_${page}`;

export const resourceIdForProjectsUnderTeamLoader = ({
  roleId,
  page = 1,
  companyId,
}: {
  roleId: number;
  page?: number;
  companyId?: number;
}): string =>
  `${roleIdFormat(roleId)}${
    companyId != null ? `_${companyIdFormat(companyId)}` : ''
  }_projects_under_team_loader_page_${page}`;

export const RESOURCE_ID_FOR_PEOPLE_LOADER_PREFIX = 'people_loader_page_';

export const resourceIdForPeopleLoader = (page?: number): string =>
  `${RESOURCE_ID_FOR_PEOPLE_LOADER_PREFIX}${page}`;

export const RESOURCE_ID_FOR_PROJECT_LOADER_PREFIX = 'project_loader_page_';

export const resourceIdForProjectLoader = (page: number): string =>
  `${RESOURCE_ID_FOR_PROJECT_LOADER_PREFIX}${page}`;

export const resourceIdForResourceUnderProjectLoader = ({
  projectId,
  page,
  budgetId,
}: {
  projectId: number;
  page?: number;
  budgetId?: number;
}): string =>
  `${projectIdFormat(projectId)}${
    budgetId ? `_${budgetIdFormat(budgetId)}` : ''
  }_resource_under_project_loader_page_${page}`;

export const resourceIdForAssignProject = (
  parentType: ResourceType,
  userIdOrTeamId: number,
): string =>
  `${
    parentType === ResourceType.User
      ? userIdFormat(userIdOrTeamId)
      : teamIdFormat(userIdOrTeamId)
  }_assign_project`;

export const resourceIdForAssignBudget = (
  parentType: ResourceType,
  userIdOrTeamId: number,
  projectId: number,
  referenceTeamId?: number,
): string =>
  `${
    parentType === ResourceType.User
      ? userIdFormat(userIdOrTeamId)
      : teamIdFormat(userIdOrTeamId)
  }_project-${projectId}${
    referenceTeamId ? `_${referenceTeamIdFormat(referenceTeamId)}` : ''
  }_assign_budget`;

export const resourceIdForTeamHeader = (): string =>
  SchedulerResourceType.TeamHeader;

export const resourceIdForPartnerHeader = (): string => 'partner_header';

export const resourceIdForExpandableRole = (
  roleId: number,
  companyId?: number,
): string =>
  `${roleIdFormat(roleId)}${
    companyId != null ? `_${companyIdFormat(companyId)}` : ''
  }`;
export const resourceIdForDemandResource = (
  roleId: number,
  resourceType: SchedulerResourceType,
): string => `${resourceType}_${roleId}`;

const ADDITIONAL_HOURS_TEAM_ID_MOD = BigInt(Number.MAX_SAFE_INTEGER);

export const generateAdditionalHoursTeamId = (
  projectId: number,
  roleId: number,
  userId: number,
): number => {
  const key = `${projectId}\0${roleId}\0${userId}`;
  let h = BigInt(0);

  for (let i = 0; i < key.length; i += 1) {
    h =
      (h * BigInt(131) + BigInt(key.charCodeAt(i))) %
      ADDITIONAL_HOURS_TEAM_ID_MOD;
  }

  const n = h === BigInt(0) ? BigInt(1) : h;

  return Number(n);
};

export const generateResourceRequestId = (
  projectId: number | string,
  resourceRequestType: string,
  teamId?: number,
  assigneeType?: string,
  assigneeId?: number | string,
  projectFinancialsBudgetId?: number,
): string => {
  const budgetPrefix = projectFinancialsBudgetId
    ? `_${budgetIdFormat(projectFinancialsBudgetId)}`
    : '';

  if (resourceRequestType === 'EXTENSION') {
    return `project-${projectId}${budgetPrefix}_team-${teamId}`;
  }

  return `project-${projectId}${budgetPrefix}_${assigneeType}-${assigneeId}`;
};

export const idForTeamAllocationSummaryEvent = (
  roleId: number,
  startDate: DateType,
  viewPreset: ViewPresetId,
  companyId?: number,
): string =>
  `role-${roleId}_${
    companyId != null ? `company-${companyId}_` : ''
  }${viewPreset}_${new Date(startDate).valueOf()}`;
export const idForCompanyAllocationSummaryEvent = (
  companyId: number,
  startDate: DateType,
  viewPreset: ViewPresetId,
): string =>
  `company-${companyId}_${viewPreset}_${new Date(startDate).valueOf()}`;

export const idForUserProjectAllocationSummaryEvent = (
  resourceIdentifier: UserProjectResourceIdentifier,
  projectId: number,
  startDate: DateType,
  viewPreset: ViewPresetId,
): string => {
  const { userId, teamId, referenceTeamId } = resourceIdentifier;

  if (resourceIdentifier.userId) {
    return `user-${userId}${
      teamId ? `_team-${referenceTeamId}` : ''
    }_project-${projectId}_${viewPreset}_${new Date(startDate).valueOf()}`;
  }

  if (teamId) {
    return `team-${teamId}_project-${projectId}_${viewPreset}_${new Date(
      startDate,
    ).valueOf()}`;
  }

  return '';
};

export const idForBudgetAllocationSummaryEvent = (
  budgetId: number,
  projectId: number,
  startDate: DateType,
  viewPreset: ViewPresetId,
): string =>
  `project-${projectId}_budget-${budgetId}_${viewPreset}_${new Date(
    startDate,
  ).valueOf()}`;

export const RESOURCE_ALLOCATION_SUMMARY_PREFIX = 'resource_allocation_summary';

export const idForResourceAllocationSummaryEvent = (
  resourceId: string,
  startDate: DateType,
  viewPreset: ViewPresetId,
): string =>
  `${RESOURCE_ALLOCATION_SUMMARY_PREFIX}_${resourceId}_${viewPreset}_${new Date(
    startDate,
  ).valueOf()}`;

export const idForUserAllocationSummaryEvent = (
  userId: number,
  startDate: DateType,
  viewPreset: ViewPresetId,
): string => `user-${userId}_${viewPreset}_${new Date(startDate).valueOf()}`;

export const idForTimeoffAsSummaryEvent = (
  userId: number,
  timeOffId: number,
): string => `user-${userId}_${timeOffId}`;

export const idForHolidayAsSummaryEvent = (
  userId: number,
  holidayId: number,
): string => `user-${userId}_holiday-${holidayId}`;

export const getKeyForResourceAggregation = (
  resourceIdentifier: ResourceIdentifier,
): string => {
  if (resourceIdentifier.type === ResourceType.User) {
    return `user-${resourceIdentifier.userId}`;
  }

  return `team-${resourceIdentifier.teamId}`;
};

export const getKeyForProjectAggregation = (
  projectId: number,
  resourceIdentifier: ResourceIdentifier,
): string => {
  if (resourceIdentifier.type === ResourceType.User) {
    return aggregationIdForUser(
      projectId,
      resourceIdentifier.roleId,
      resourceIdentifier.referenceTeamId,
      resourceIdentifier?.projectFinancialsBudgetId,
    );
  }

  return `project-${projectId}${
    resourceIdentifier.projectFinancialsBudgetId
      ? `_budget-${resourceIdentifier.projectFinancialsBudgetId}`
      : ''
  }`;
};
